import { isTernaryOperator } from "@skilitics/judy";
import type { PrimitiveValue } from "@skilitics/nautilus-core";
import type { ValueConfig, ValueExpression } from "@skilitics/nautilus-core-designer-data";
import { isReferencedValue, isValueTombstone } from "@skilitics/nautilus-core-designer-data";

import type { ResolveValueToConditionValue } from "../resolveValueConfigs.ts";
import type { ResolvedValueSignature } from "../resolveValueSignature.ts";
import type { ValueSubtypeDefinition } from "../valueSubtypeDefinition.ts";

export interface ValueExpressionContext {
  /**
   * The subject's type, subtype and spec. `undefined` where the subject is
   * missing or no longer resolvable — see {@link ResolveValueSignature}.
   */
  signature: ResolvedValueSignature | undefined;
  /** The definition governing the comparison, resolved from the signature. */
  definition: ValueSubtypeDefinition;
  /** Resolves an operand, so a bound one is checked exactly as it will be built. */
  resolveValue: ResolveValueToConditionValue;
}

/** How many operands the comparison needs, from the arity of its operator. */
const operandCount = (expression: ValueExpression) =>
  expression.operator && isTernaryOperator(expression.operator) ? 2 : 1;

/**
 * Whether an operand is usable: a literal the definition accepts, or a reference
 * that resolves — which is the fit check (base type plus `subtypeSatisfies`)
 * already, since resolution returns undefined for a reference that does not fit.
 *
 * A tombstoned operand never passes. Only a subject is ever tombstoned, so this
 * is defensive rather than expected.
 */
export const isValueOperandValid = (
  operand: ValueConfig | undefined,
  { signature, definition, resolveValue }: ValueExpressionContext,
): boolean => {
  if (operand === undefined || isValueTombstone(operand)) return false;
  if (isReferencedValue(operand)) {
    const requirement = signature?.subtype && definition.require?.(signature.subtype);
    return resolveValue(operand, definition.type, requirement) !== undefined;
  }
  return definition.isValid(operand as PrimitiveValue, signature?.subtype, signature?.subtypeSpec);
};

/**
 * Whether an expression says something a condition can be built from. The
 * build's own gate, and the whole of it: nothing partial is emitted.
 *
 * Valid ⇔ the subject resolves, the operator is one the definition offers, and
 * every operand its arity requires is present and usable.
 *
 * What an author is told is narrower, and knowingly so. The form errors an
 * operand the definition rejects, and the condition's `isValid` greys the whole
 * where the subject is a tombstone — but neither reaches every reason this
 * abstains, so an expression can read as authored and still build to nothing.
 */
export const isValueExpressionValid = (expression: ValueExpression, context: ValueExpressionContext): boolean => {
  const { signature, definition } = context;
  if (!signature || !expression.subject) return false;
  if (!expression.operator || !definition.operators.includes(expression.operator)) return false;

  if (!isValueOperandValid(expression.value, context)) return false;
  return operandCount(expression) < 2 || isValueOperandValid(expression.value2, context);
};
